import React, { useState, useEffect, useMemo, useCallback } from "react";
import api from "../services/api";
import { getUser } from "../services/auth";
import {
  Autocomplete,
  TextField,
  Chip,
  LinearProgress,
  Snackbar,
  Alert,
  IconButton,
  Box,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import "bootstrap/dist/css/bootstrap.min.css";
import IntakeForm from "./IntakeForm";

/* Employee Panel (container image upload + new intake).
 *
 * This is the former standalone /admin page, now rendered INSIDE the main
 * sidebar shell as the "EMPLOYEE PANEL" module — so a user lands here like any
 * other module and can switch to their other modules (e.g. Wire Chopping) from
 * the sidebar. The page-level header / logout were dropped because the Main
 * shell already provides them; the EN/ES switcher stays for warehouse staff.
 */
const LANG_KEY = "admin_lang";
const MAX_IMAGE_SIZE_BYTES = 15 * 1024 * 1024;  // 15 MB per file
const MAX_IMAGES_PER_UPLOAD = 50;

export default function EmployeePanel() {
  const [currentUser, setCurrentUser] = useState(null);
  const [containers, setContainers] = useState([]);
  const [records, setRecords] = useState([]);
  const [selectedContainerObj, setSelectedContainerObj] = useState(null);
  const [items, setItems] = useState([]);
  const [imagesToUpload, setImagesToUpload] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [search, setSearch] = useState("");
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem(LANG_KEY);
    if (saved === "en" || saved === "es") return saved;
    // Default: ES for employees, EN for admins.
    const u = getUser();
    return u?.role === "admin" ? "en" : "es";
  });
  const [toast, setToast] = useState({ open: false, message: "", severity: "info" });
  const [activeView, setActiveView] = useState("containers"); // "containers" | "intake"

  const showToast = useCallback((message, severity = "info") => {
    setToast({ open: true, message, severity });
  }, []);

  const closeToast = () => setToast((t) => ({ ...t, open: false }));

  // Persist language choice
  useEffect(() => {
    try { localStorage.setItem(LANG_KEY, lang); } catch (_) { /* ignore */ }
  }, [lang]);

  const texts = {
    en: {
      upload: "Upload Container Images",
      uploading: "Uploading…",
      selectContainer: "Select Container",
      selectImages: "Select Images",
      preview: "Selected images",
      filesSelected: "file(s) selected",
      download: "Download ZIP",
      delete: "Delete",
      noContainers: "No containers found",
      noItems: "—",
      confirmDelete: "Delete this container?",
      selectAtLeastOne: "Select at least one image",
      selectContainerAlert: "Select a container",
      uploadSuccess: "image(s) uploaded",
      uploadFail: "Upload failed",
      loadContainersFail: "Failed to load containers",
      loadRecordsFail: "Failed to load records",
      downloadFail: "Download failed",
      deleteSuccess: "Container record deleted",
      deleteFail: "Delete failed",
      tooLarge: "is too large (max 15 MB)",
      tooMany: `Pick at most ${MAX_IMAGES_PER_UPLOAD} files at a time`,
      items: "Items",
      search: "Search Container Number…",
      tabContainers: "Container Dashboard",
      tabIntake: "New Intake",
    },
    es: {
      upload: "Subir imágenes de contenedor",
      uploading: "Subiendo…",
      selectContainer: "Seleccionar contenedor",
      selectImages: "Seleccionar imágenes",
      preview: "Imágenes seleccionadas",
      filesSelected: "archivo(s) seleccionado(s)",
      download: "Descargar ZIP",
      delete: "Eliminar",
      noContainers: "No se encontraron contenedores",
      noItems: "—",
      confirmDelete: "¿Eliminar este contenedor?",
      selectAtLeastOne: "Selecciona al menos una imagen",
      selectContainerAlert: "Selecciona un contenedor",
      uploadSuccess: "imagen(es) subidas",
      uploadFail: "Error al subir imágenes",
      loadContainersFail: "Error al cargar contenedores",
      loadRecordsFail: "Error al cargar registros",
      downloadFail: "Error al descargar",
      deleteSuccess: "Registro de contenedor eliminado",
      deleteFail: "Error al eliminar",
      tooLarge: "es demasiado grande (máximo 15 MB)",
      tooMany: `Selecciona como máximo ${MAX_IMAGES_PER_UPLOAD} archivos`,
      items: "Artículos",
      search: "Buscar número de contenedor…",
      tabContainers: "Contenedores",
      tabIntake: "Nueva Recepción",
    },
  };
  const t = texts[lang];

  // -------- Load on mount --------
  useEffect(() => {
    const user = getUser();
    if (!user) return; // the Main shell already guards auth
    user.name = user.name
      ? user.name.charAt(0).toUpperCase() + user.name.slice(1)
      : (user.username || "").charAt(0).toUpperCase() + (user.username || "").slice(1);
    setCurrentUser(user);
    loadContainers();
    loadRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadContainers = async () => {
    try {
      const res = await api.get("/containerimages/active");
      setContainers(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast(`${t.loadContainersFail}: ${err.response?.data?.error || err.message}`, "error");
    }
  };

  const loadRecords = async () => {
    try {
      const res = await api.get("/containerimages/records");
      setRecords(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showToast(`${t.loadRecordsFail}: ${err.response?.data?.error || err.message}`, "error");
    }
  };

  // -------- Image preview lifecycle --------
  useEffect(() => {
    return () => {
      imagePreviews.forEach((p) => URL.revokeObjectURL(p.url));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleImageChange = (e) => {
    const files = [...e.target.files];
    if (!files.length) return;

    if (files.length > MAX_IMAGES_PER_UPLOAD) {
      showToast(t.tooMany, "warning");
      e.target.value = "";
      return;
    }

    const tooBig = files.find((f) => f.size > MAX_IMAGE_SIZE_BYTES);
    if (tooBig) {
      showToast(`${tooBig.name} ${t.tooLarge}`, "error");
      e.target.value = "";
      return;
    }

    imagePreviews.forEach((p) => URL.revokeObjectURL(p.url));

    setImagesToUpload(files);
    setImagePreviews(
      files.map((f) => ({
        name: f.name,
        url: URL.createObjectURL(f),
        size: f.size,
      }))
    );
    e.target.value = "";
  };

  const removeImage = (idx) => {
    URL.revokeObjectURL(imagePreviews[idx].url);
    setImagesToUpload((prev) => prev.filter((_, i) => i !== idx));
    setImagePreviews((prev) => prev.filter((_, i) => i !== idx));
  };

  // -------- Upload --------
  const handleUpload = async () => {
    if (!selectedContainerObj) {
      showToast(t.selectContainerAlert, "warning");
      return;
    }
    if (!imagesToUpload.length) {
      showToast(t.selectAtLeastOne, "warning");
      return;
    }
    if (!currentUser?.id) {
      showToast("Session expired — please log in again.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("ContainerID", selectedContainerObj.ContainerID);
    formData.append("EmployeeID", currentUser.id);
    imagesToUpload.forEach((f) => formData.append("images", f));

    setUploading(true);
    setUploadProgress(0);

    try {
      await api.post("/containerimages/upload-images", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          const pct = e.total ? Math.round((e.loaded / e.total) * 100) : 0;
          setUploadProgress(pct);
        },
      });
      showToast(`${imagesToUpload.length} ${t.uploadSuccess}`, "success");

      imagePreviews.forEach((p) => URL.revokeObjectURL(p.url));
      setImagesToUpload([]);
      setImagePreviews([]);
      setSelectedContainerObj(null);
      setItems([]);
      loadRecords();
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message;
      showToast(`${t.uploadFail}: ${msg}`, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // -------- Container row actions --------
  const handleDeleteContainer = async (ContainerID) => {
    if (!window.confirm(t.confirmDelete)) return;
    try {
      await api.delete(`/containerimages/${ContainerID}`);
      loadRecords();
      loadContainers();
      if (selectedContainerObj?.ContainerID === ContainerID) {
        setSelectedContainerObj(null);
        setItems([]);
      }
      showToast(t.deleteSuccess, "success");
    } catch (err) {
      showToast(`${t.deleteFail}: ${err.response?.data?.error || err.message}`, "error");
    }
  };

  const handleDownload = async (containerId, containerNo) => {
    try {
      const response = await api.get(`/containerimages/${containerId}/download`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${containerNo}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast(`${t.downloadFail}: ${err.response?.data?.error || err.message}`, "error");
    }
  };

  // -------- Container selection --------
  const handleContainerChange = async (container) => {
    setSelectedContainerObj(container);
    setItems([]);
    if (!container?.BookingID) return;
    try {
      const res = await api.get(`/containerimages/bookinglines/${container.BookingID}`);
      setItems(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.warn("Failed to load items for booking:", err);
      setItems([]);
    }
  };

  // -------- Search --------
  const filteredRecords = useMemo(
    () =>
      records.filter((r) =>
        (r.ContainerNo || "").toLowerCase().includes(search.toLowerCase())
      ),
    [records, search]
  );

  // -------- Distinct materials for the selected container --------
  /* The one part of this screen a loader has to read and act on. If he is
   * working in Spanish the material must be in Spanish too - the rest of the
   * page being translated while the material list stays in English is worse
   * than no translation at all, because it looks finished.
   *
   * Spanish names are typed per material in Product Management. Anything not
   * translated yet falls back to the English name, so the chip is never empty,
   * and the English name is kept on the tooltip either way. */
  const distinctMaterials = useMemo(() => {
    const map = new Map();
    for (const it of items) {
      const name = (it.item || "").trim();
      if (!name) continue;
      if (!map.has(name)) {
        map.set(name, { name, es: (it.item_es || "").trim(), pos: new Set() });
      }
      if (it.PO) map.get(name).pos.add(String(it.PO));
    }
    return Array.from(map.values()).map((m) => ({
      name: m.name,
      label: lang === "es" && m.es ? m.es : m.name,
      translated: Boolean(lang === "es" && m.es),
      pos: Array.from(m.pos),
    }));
  }, [items, lang]);

  return (
    <div className="px-1">
      {/* LANG SWITCHER */}
      <div className="d-flex justify-content-end mb-2 flex-wrap gap-1">
        <button
          className={`btn btn-${lang === "en" ? "primary" : "outline-primary"} btn-sm me-2`}
          onClick={() => setLang("en")}
        >EN</button>
        <button
          className={`btn btn-${lang === "es" ? "primary" : "outline-primary"} btn-sm`}
          onClick={() => setLang("es")}
        >ES</button>
      </div>

      {/* VIEW SWITCHER */}
      <div className="d-flex gap-2 mb-3">
        <button
          className={`btn btn-${activeView === "containers" ? "primary" : "outline-primary"} btn-sm`}
          onClick={() => setActiveView("containers")}
        >{t.tabContainers}</button>
        <button
          className={`btn btn-${activeView === "intake" ? "primary" : "outline-primary"} btn-sm`}
          onClick={() => setActiveView("intake")}
        >{t.tabIntake}</button>
      </div>

      {activeView === "intake" && (
        <div className="card shadow-sm p-3 mb-4">
          <IntakeForm lang={lang} />
        </div>
      )}

      {activeView === "containers" && <>

      {/* UPLOAD CARD */}
      <div className="card shadow-sm p-3 mb-4">
        <h5>{t.upload}</h5>

        <div className="row g-3">
          {/* Container Autocomplete */}
          <div className="col-12 col-md-4">
            <label className="form-label small mb-1">{t.selectContainer}</label>
            <Autocomplete
              options={containers}
              getOptionLabel={(o) => o?.ContainerNo || ""}
              renderOption={(props, o) => (
                <li {...props} key={o.ContainerID}>
                  <Box sx={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                    <span>{o.ContainerNo}</span>
                    {o.BookingNo && (
                      <small style={{ color: "#888" }}>Booking {o.BookingNo}</small>
                    )}
                  </Box>
                </li>
              )}
              isOptionEqualToValue={(opt, val) => opt.ContainerID === val.ContainerID}
              value={selectedContainerObj}
              onChange={(_, v) => handleContainerChange(v)}
              disabled={uploading}
              renderInput={(p) => (
                <TextField {...p} placeholder={t.selectContainer} size="small" />
              )}
              fullWidth
            />
          </div>

          {/* Items as Chips */}
          <div className="col-12 col-md-4">
            <label className="form-label small mb-1">{t.items}</label>
            <Box
              sx={{
                minHeight: 40,
                p: 0.75,
                border: 1,
                borderColor: "#ced4da",
                borderRadius: 1,
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                alignItems: "center",
                bgcolor: "#fff",
              }}
            >
              {distinctMaterials.length === 0 ? (
                <span style={{ color: "#999", fontSize: "0.875rem" }}>{t.noItems}</span>
              ) : (
                distinctMaterials.map((m) => (
                  <Chip
                    key={m.name}
                    label={m.label}
                    size="small"
                    color="primary"
                    variant="outlined"
                    title={[
                      m.translated ? m.name : null,
                      m.pos.length ? `PO: ${m.pos.join(", ")}` : null,
                    ].filter(Boolean).join("  ·  ") || undefined}
                  />
                ))
              )}
            </Box>
          </div>

          {/* File Input */}
          <div className="col-12 col-md-4">
            <label className="form-label small mb-1">{t.selectImages}</label>
            <input
              type="file"
              multiple
              accept="image/*"
              className="form-control"
              onChange={handleImageChange}
              disabled={uploading}
            />
            {imagesToUpload.length > 0 && (
              <small className="text-muted">
                {imagesToUpload.length} {t.filesSelected}
              </small>
            )}
          </div>
        </div>

        {/* Image Previews */}
        {imagePreviews.length > 0 && (
          <div className="mt-3">
            <small className="text-muted d-block mb-2">{t.preview}</small>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.25 }}>
              {imagePreviews.map((p, idx) => (
                <Box key={p.url} sx={{ position: "relative", width: 88, height: 88 }}>
                  <img
                    src={p.url}
                    alt={p.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 4,
                      border: "1px solid #ddd",
                      display: "block",
                    }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => removeImage(idx)}
                    disabled={uploading}
                    aria-label="remove"
                    sx={{
                      position: "absolute",
                      top: -8,
                      right: -8,
                      width: 22,
                      height: 22,
                      bgcolor: "error.main",
                      color: "white",
                      "&:hover": { bgcolor: "error.dark" },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
              ))}
            </Box>
          </div>
        )}

        {/* Upload Progress */}
        {uploading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <small className="text-muted">{uploadProgress}% — {t.uploading}</small>
          </Box>
        )}

        <button
          className="btn btn-primary mt-3 w-100"
          onClick={handleUpload}
          disabled={uploading || !selectedContainerObj || imagesToUpload.length === 0}
        >
          {uploading ? t.uploading : t.upload}
        </button>
      </div>

      {/* SEARCH */}
      <input
        type="text"
        placeholder={t.search}
        className="form-control mb-3"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {/* RECORDS TABLE */}
      <div className="table-responsive">
        <table className="table table-striped table-bordered align-middle">
          <thead className="table-dark">
            <tr>
              <th>Container #</th>
              <th>Total Images</th>
              <th>{t.download}</th>
              <th>{t.delete}</th>
            </tr>
          </thead>
          <tbody>
            {filteredRecords.length > 0 ? (
              filteredRecords.map((r) => (
                <tr key={r.ContainerID}>
                  <td>{r.ContainerNo}</td>
                  <td>{r.TotalImages}</td>
                  <td>
                    {r.TotalImages > 0 ? (
                      <button
                        className="btn btn-success btn-sm"
                        onClick={() => handleDownload(r.ContainerID, r.ContainerNo)}
                      >
                        {t.download}
                      </button>
                    ) : (
                      <span className="text-muted">No images</span>
                    )}
                  </td>
                  <td>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => handleDeleteContainer(r.ContainerID)}
                    >
                      {t.delete}
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="4" className="text-center text-muted">
                  {t.noContainers}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      </>}

      {/* TOAST */}
      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={closeToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={toast.severity}
          onClose={closeToast}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
}
